<template>
  <b-modal ref="modal" :title="isEdit ? 'Edit Chart' : 'Create Chart'" size="xl" no-close-on-backdrop @hidden="draft = null">
    <div v-if="draft">
      <div class="form-group">
        <label class="small font-weight-bold" for="custom-chart-name">Chart name</label>
        <input
          id="custom-chart-name"
          v-model="draft.name"
          type="text"
          class="form-control"
          maxlength="80"
          placeholder="e.g. Biwin 8 RPM this week vs last week"
        >
      </div>
      <div class="form-row">
        <div class="form-group col-md-4">
          <label class="small font-weight-bold">Range</label>
          <select v-model="rangePreset" class="form-control">
            <option v-for="p in rangePresets" :key="p.value" :value="p.value">{{ p.label }}</option>
          </select>
        </div>
        <div v-if="draft.range.preset === 'custom'" class="form-group col-md-5">
          <label class="small font-weight-bold">Custom range</label>
          <date-time-picker v-model="customRange" is-range masks="DD-MM-YYYY HH:mm" />
        </div>
        <div class="form-group col-md-3">
          <label class="small font-weight-bold">Interval</label>
          <select v-model="draft.interval" class="form-control">
            <option value="auto">Auto ({{ autoIntervalText }})</option>
            <option value="minute">By minute</option>
            <option value="hour">Hourly</option>
            <option value="day">Daily</option>
          </select>
          <small :class="pointCount > warnPoints ? 'text-warning' : 'text-muted'">
            ~{{ pointCount.toLocaleString('en-US') }} points per series{{ pointCount > warnPoints ? ' — this may take a while' : '' }}
          </small>
        </div>
      </div>

      <label class="small font-weight-bold d-block mb-0">Series</label>
      <series-editor
        v-for="(s, i) in draft.series"
        :key="s.id"
        v-model="draft.series[i]"
        :index="i"
        :assets="assets"
        :device-name-by-uid="deviceNameByUid"
        @remove="removeSeries(i)"
      />
      <button type="button" class="btn btn-sm btn-outline-primary mt-2" :disabled="draft.series.length >= maxSeries" @click="addSeries">
        Add series
      </button>

      <ul v-if="showErrors && errors.length" class="text-danger small mt-3 mb-0 pl-3">
        <li v-for="e in errors" :key="e">{{ e }}</li>
      </ul>
    </div>

    <template #modal-footer>
      <button type="button" class="btn btn-secondary" @click="close">Cancel</button>
      <button type="button" class="btn btn-mantraz" :disabled="!draft" @click="save">Save</button>
    </template>
  </b-modal>
</template>

<script>
import DateTimePicker from '@/components/directives/DateTimePicker.vue';
import SeriesEditor from '@/components/customChart/SeriesEditor.vue';
import {
  RANGE_PRESETS, INTERVAL_TEXT, MAX_SERIES, WARN_POINTS,
  resolveMainRange, resolveInterval, estimatePoints, validateChart, createSeries, createChart,
} from '@/util/customChartModel';

function copy(value) {
  return JSON.parse(JSON.stringify(value));
}

export default {
  name: 'ChartBuilderModal',
  components: { DateTimePicker, SeriesEditor },
  props: {
    assets: { type: Array, default: () => [] },
    deviceNameByUid: { type: Object, default: null },
  },
  data() {
    return {
      draft: null,
      isEdit: false,
      showErrors: false,
      rangePresets: RANGE_PRESETS,
      maxSeries: MAX_SERIES,
      warnPoints: WARN_POINTS,
    };
  },
  computed: {
    mainRange() {
      return this.draft ? resolveMainRange(this.draft.range, Date.now()) : null;
    },
    autoIntervalText() {
      return this.mainRange ? INTERVAL_TEXT[resolveInterval('auto', this.mainRange)] : '';
    },
    pointCount() {
      if (!this.mainRange) return 0;
      return estimatePoints(this.mainRange, resolveInterval(this.draft.interval, this.mainRange));
    },
    errors() {
      return this.draft ? validateChart(this.draft, { nowMs: Date.now(), deviceNameByUid: this.deviceNameByUid }) : [];
    },
    rangePreset: {
      get() {
        return this.draft.range.preset;
      },
      set(preset) {
        if (preset === 'custom') {
          const { startMs, endMs } = resolveMainRange(this.draft.range, Date.now());
          this.draft.range = { preset: 'custom', startMs, endMs };
        } else {
          this.draft.range = { preset };
        }
      },
    },
    customRange: {
      get() {
        return { start: new Date(this.draft.range.startMs), end: new Date(this.draft.range.endMs) };
      },
      set(value) {
        this.draft.range = { preset: 'custom', startMs: +value.start, endMs: +value.end };
      },
    },
  },
  methods: {
    open(chart, { companyId, order }) {
      this.isEdit = !!chart;
      this.showErrors = false;
      this.draft = chart ? copy(chart) : createChart({ companyId, order, nowMs: Date.now() });
      this.$refs.modal.show();
    },
    close() {
      this.$refs.modal.hide();
    },
    addSeries() {
      this.draft.series.push(createSeries(this.draft.series));
    },
    removeSeries(i) {
      this.draft.series.splice(i, 1);
    },
    // Pesan validasi baru muncul setelah Save pertama, biar form baru gak penuh merah.
    save() {
      if (!this.draft) return;
      this.showErrors = true;
      if (this.errors.length) return;
      this.$emit('save', copy({ ...this.draft, name: this.draft.name.trim() }));
      this.close();
    },
  },
};
</script>
